import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";

// Declare global constants injected by Vite define
declare const API_BASE_URL: string;

type RoleContextType = {
  roles: string[];
  loading: boolean;
  hasUserRole: boolean;
};

const RoleContext = createContext<RoleContextType>({
  roles: [],
  loading: true,
  hasUserRole: false,
});

export const RoleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const { getAccessTokenSilently, isLoading: authLoading, isAuthenticated } = useAuth0();

  useEffect(() => {
    const fetchRoles = async () => {
      if (authLoading || !isAuthenticated) return;

      try {
        const token = await getAccessTokenSilently();

        if (!token) {
          throw new Error("No Auth0 token found");
        }

        console.log("Fetching roles from /api/auth/me");

        const res = await axios.post(
          `${API_BASE_URL}/api/auth/me`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setRoles(res.data.roles || []);
      } catch (e) {
        console.error("Failed to fetch roles", e);
        setRoles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, [authLoading, isAuthenticated]);

  const hasUserRole = roles.some(role => role.toLowerCase() === "user");

  return (
    <RoleContext.Provider value={{ roles, loading, hasUserRole }}>
      {children}
    </RoleContext.Provider>
  );
};

export const useRoles = () => useContext(RoleContext);
